import React, { useState, useMemo } from 'react';
import { 
    Dialog, 
    DialogContent, 
    DialogTitle, 
    LinearProgress, 
    Tooltip 
} from "@mui/material";
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Add, EditNote } from '@mui/icons-material';

import FormikControl from '../../../../../../components/FormikControl/FormikControl';
import partyServices from '../partyServices';
import { useAlert } from '../../../../../../context/AlertContext';
import { useAppData } from '../../../../../../context/AppDataContext';

/**
 * PartyForm Component
 * 
 * Renders a modal dialog containing a dynamic form powered by Formik and Yup.
 * Handles both Creation and Updates for ERP Party entities.
 */
function PartyForm({ data, callBack, callBackWithId, partyRole }) {
    // Application Context Hooks
    const { AppAlert } = useAlert();
    const { masters, getPartyDD } = useAppData();

    // Local Component State
    const [formValues, setFormValues] = useState(null);
    const [dialogStatus, setDialogStatus] = useState(false);
    const [savingInProgress, setSavingStatus] = useState(false);

    // Dynamic Section Toggles
    const [differentSupplyState, setDifferentSupplyState] = useState(false);
    const [withAddressDetails, setWithAddressDetails] = useState(false);

    /**
     * Default initial values for creating a new Party entry.
     */
    const initialValues = {
        partyRole: partyRole ? partyRole.value : 1,
        displayName: "",
        contactPerson: "",
        phone: "",
        email: "",
        creditLimit: "",

        gstin: "",
        panNo: "",
        registrationType: "",
        tdsApplicable: false,

        city: "",
        state: "",
        zip: "",
        address: "",
        supplyState: "",
    };

    /**
     * Prepares form values from existing record data for updating.
     */
    const updateData = () => {
        let autoFill = {
            id: data.id,
            ledgerId: data.ledgerId,
            displayName: data.displayName || "",
            contactPerson: data.contactPerson || "",
            phone: data.phone || "",
            email: data.email || "",
            creditLimit: data.creditLimit || "",

            gstin: "",
            panNo: "",
            registrationType: "",
            tdsApplicable: false,
        };

        if (data.taxDetails !== null && data.taxDetails !== undefined) {
            autoFill.gstin = data.taxDetails.gstin || "";
            autoFill.panNo = data.taxDetails.panNo || "";
            autoFill.registrationType = data.taxDetails.registrationType || "";
            autoFill.tdsApplicable = Boolean(data.taxDetails.tdsApplicable);
        }

        setFormValues(autoFill);
        setDialogStatus(true);
    };

    /**
     * Re-computed Validation Schema
     * Re-evaluates whenever `withAddressDetails` or `differentSupplyState` state changes.
     */
    const validationRule = useMemo(() => {
        return Yup.object({
            displayName: Yup.string().trim().required("Display name is required"),
            phone: Yup.string()
                .required("Phone Number Is Required")
                .transform(value => (value === "" ? null : value))
                .matches(/^[0-9]{10}$/, "Phone number must be exactly 10 digits"),
            email: Yup.string()
                .nullable()
                .transform(value => (value === "" ? null : value))
                .email("Invalid email format"),
            gstin: Yup.string()
                .nullable()
                .transform(value => (value === "" ? null : value))
                .matches(
                    /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/i,
                    "Invalid GSTIN structure (e.g. 22AAAAA0000A1Z5)"
                ),
            panNo: Yup.string()
                .nullable()
                .transform(value => (value === "" ? null : value))
                .matches(
                    /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/i,
                    "Invalid PAN format (e.g. ABCDE1234F)"
                ),
            creditLimit: Yup.number()
                .typeError("Credit Limit must be a number")
                .min(0, "Credit Limit cannot be negative")
                .nullable(),

            // CONDITIONAL ADDRESS VALIDATIONS
            city: withAddressDetails 
                ? Yup.string().required("City is required") 
                : Yup.string().nullable(),

            state: withAddressDetails 
                ? Yup.string().required("State is required") 
                : Yup.string().nullable(),

            zip: withAddressDetails 
                ? Yup.string()
                    //.required("Pincode is required")
                    .matches(/^[0-9]{6}$/, "Pincode must be exactly 6 digits") 
                : Yup.string().nullable(),

            address: withAddressDetails 
                ? Yup.string()
                    //.required("Address is required") 
                : Yup.string().nullable(),

            supplyState: (withAddressDetails && differentSupplyState)
                ? Yup.string().required("Supply state is required")
                : Yup.string().nullable()
        });
    }, [withAddressDetails, differentSupplyState]);

    /**
     * Resets form state and opens dialog for creating a new entry.
     */
    const addNew = () => {
        setFormValues(initialValues);
        setWithAddressDetails(false);
        setDifferentSupplyState(false);
        setDialogStatus(true);
    };

    /**
     * Closes dialog and releases loading lock state.
     */
    const handleClose = () => {
        setDialogStatus(false);
        setSavingStatus(false);
    };

    /**
     * Submits party details payload to backend.
     */
    const onSubmit = (values) => {
        setSavingStatus(true);
        let partyDetails = { ...values };
        
        partyDetails.withAddressDetails = withAddressDetails;
        if(partyDetails.supplyState == "" || partyDetails.supplyState == 0){
            partyDetails.supplyState = partyDetails.state;
        }

        partyServices.save(partyDetails)
            .then(res => {
                if (res.error) {
                    AppAlert(res.msg || "Operation failed", "error");
                } else {
                    AppAlert(res.msg || "Saved successfully");
                    setDialogStatus(false);
                    getPartyDD();
                    
                    if (callBackWithId) {
                        callBackWithId(res.id);
                    } else if (callBack) {
                        callBack();
                    }
                }
            })
            .catch(error => {
                AppAlert(error?.msg || "Server communication error", "error");
            })
            .finally(() => {
                setSavingStatus(false);
            });
    };

    return (
        <>
            {/* Trigger Buttons */}
            {!data && (
                <Tooltip title="Add New">
                    <button type="button" className="btn btn-primary" onClick={addNew}>
                        <Add /> Add {partyRole?.label || 'Party'}
                    </button>
                </Tooltip>
            )}
            {data && (
                <Tooltip title="Update">
                    <button type="button" className="btn btn-primary" onClick={updateData}>
                        <EditNote />
                    </button>
                </Tooltip>
            )}

            {/* Modal Dialog Form */}
            <Dialog
                open={dialogStatus}
                keepMounted
                onClose={handleClose}
                fullWidth={true}
                maxWidth="md"
                aria-describedby="alert-dialog-slide-description"
            >
                <Formik
                    initialValues={formValues || initialValues}
                    validationSchema={validationRule}
                    onSubmit={onSubmit}
                    enableReinitialize
                    validateOnMount
                >
                    {(formik) => (
                        <Form>
                            <DialogTitle>
                                {data ? "Edit Party Profile" : `Add New ${partyRole?.label || 'Party'}`}
                            </DialogTitle>
                            <DialogContent>
                                {/* Primary Profile Information */}
                                <div className="row p-3">
                                    <div className="col-md-6 my-2">
                                        {dialogStatus && (
                                            <FormikControl
                                                control="input"
                                                formik={formik}
                                                type="text"
                                                autoFocus
                                                label="Display Name (Company or Customer Name) *"
                                                name="displayName"
                                            />
                                        )}
                                    </div>
                                    <div className="col-md-6 my-2">
                                        <FormikControl
                                            control="input"
                                            formik={formik}
                                            type="text"
                                            label="Phone *"
                                            name="phone"
                                        />
                                    </div>
                                    <div className="col-md-6 my-2">
                                        <FormikControl
                                            control="input"
                                            formik={formik}
                                            type="text"
                                            label="Contact Person"
                                            name="contactPerson"
                                        />
                                    </div>
                                    <div className="col-md-6 my-2">
                                        <FormikControl
                                            control="input"
                                            formik={formik}
                                            type="text"
                                            label="Email"
                                            name="email"
                                        />
                                    </div>

                                    {/* Tax Metadata Section */}
                                    <hr className="my-3" />
                                    <div className="col-md-4 my-2">
                                        <FormikControl
                                            control="input"
                                            formik={formik}
                                            type="text"
                                            label="GSTIN"
                                            name="gstin"
                                        />
                                    </div>

                                    <div className="col-md-4 my-2">
                                        <FormikControl
                                            control="input"
                                            formik={formik}
                                            type="text"
                                            label="PAN No"
                                            name="panNo"
                                        />
                                    </div>

                                    <div className="col-md-4 my-2">
                                        <FormikControl
                                            control="autoselect"
                                            formik={formik}
                                            label="Registration Type"
                                            name="registrationType"
                                            options={masters['registration-type'] || []}
                                            multiple={false}
                                        />
                                    </div>

                                    <div className="col-md-4 my-2">
                                        <FormikControl
                                            control="switch"
                                            formik={formik}
                                            label="TDS Applicable"
                                            name="tdsApplicable"
                                        />
                                    </div>
                                </div>

                                {/* Conditional Address Section (Creation Mode Only) */}
                                {!data && (
                                    <>
                                        {withAddressDetails ? (
                                            <div className="row">
                                                <hr className="my-3" />
                                                <div className="col-md-4 my-2">
                                                    <FormikControl
                                                        control="input"
                                                        formik={formik}
                                                        type="text"
                                                        label="City *"
                                                        name="city"
                                                    />
                                                </div>

                                                <div className="col-md-4 my-2">
                                                    <FormikControl
                                                        control="autoselect"
                                                        formik={formik}
                                                        label="State *"
                                                        name="state"
                                                        options={masters['indian-state'] || []}
                                                        multiple={false}
                                                    />
                                                </div>

                                                <div className="col-md-4 my-2">
                                                    <FormikControl
                                                        control="input"
                                                        formik={formik}
                                                        type="text"
                                                        label="Pincode"
                                                        name="zip"
                                                    />
                                                </div>

                                                <div className="col-md-12 my-2">
                                                    <FormikControl
                                                        control="input"
                                                        formik={formik}
                                                        type="text"
                                                        multiline={true}
                                                        label="Address"
                                                        name="address"
                                                    />
                                                </div>

                                                {differentSupplyState ? (
                                                    <div className="col-md-12 my-2 mt-3">
                                                        <FormikControl
                                                            control="autoselect"
                                                            formik={formik}
                                                            label="Supply To *"
                                                            name="supplyState"
                                                            options={masters['indian-state'] || []}
                                                            multiple={false}
                                                        />
                                                    </div>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary mt-3"
                                                        onClick={() => setDifferentSupplyState(true)}
                                                    >
                                                        Use Different State for Supply
                                                    </button>
                                                )}
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                className="btn btn-primary mt-3"
                                                onClick={() => setWithAddressDetails(true)}
                                            >
                                                Add Address Details
                                            </button>
                                        )}
                                    </>
                                )}

                                {/* Accounting Credit Constraints (Update Mode Only) */}
                                {data && (
                                    <div className="row">
                                        <div className="col-md-6 my-2">
                                            <FormikControl
                                                control="input"
                                                formik={formik}
                                                type="text"
                                                label="Credit Limit"
                                                name="creditLimit"
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Action Buttons and Progress Indicator */}
                                <div className="row mt-3">
                                    <div className="col-md-12">
                                        {savingInProgress ? (
                                            <LinearProgress />
                                        ) : (
                                            <>
                                                <button
                                                    className="btn btn-primary float-end"
                                                    type="submit"
                                                    disabled={!formik.isValid}
                                                >
                                                    Save
                                                </button>
                                                <button
                                                    className="btn btn-outline-primary"
                                                    type="button"
                                                    onClick={handleClose}
                                                >
                                                    Cancel
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </DialogContent>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </>
    );
}

export default PartyForm;